import {
  useCanvasFileRecords,
  useFlowSessionFileRecords,
} from "@/api/fileRecords";
import type { DraggableData } from "@/dnd/ids";
import { buildCopyPayloadFromRecord } from "@/dnd/payloads";
import {
  getNodeTitle,
  type ContentItem,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/canvasRailContentsModel";
import { useCanvasRailContents } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/useCanvasRailContents";
import { FilesRailContextDraggableFileRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailContextDraggableFileRow";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { useFilesRailContextFileActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailContextFileActions";
import { RailExpandableRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailExpandableRow";
import {
  RAIL_LIST_ITEM_ATTR,
  RAIL_SELECTED_ATTR,
  useRailRowSelection,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import {
  useActiveFileRecordId,
  useOpenFileRecord,
} from "@/panes/file-record-detail";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import type { Node } from "@xyflow/react";
import { Layers } from "lucide-react";
import { useMemo } from "react";

/**
 * Canvas section header in the active-context rail. Draggable to a
 * workspace folder via the same `kind: "section"` payload the canvas
 * rail's SectionFolderRow emits — the dispatcher's `section_to_folder`
 * path copies the section's files into a new folder named after it.
 * Only direct file children travel; entities / widgets / sub-sections
 * have no meaningful workspace representation.
 */
function FilesRailContextSectionGroup({
  section,
  items,
  canvasId,
  children,
}: {
  section: Node;
  items: ContentItem[];
  canvasId: string;
  children: React.ReactNode;
}) {
  const title = getNodeTitle(section);

  const sectionFilePayloads = useMemo(
    () =>
      items
        .filter((it) => it.category === "file" && it.fileRecord)
        .map((it) =>
          buildCopyPayloadFromRecord(
            it.fileRecord!,
            { canvasId },
            { placed: it.placed },
          ),
        ),
    [items, canvasId],
  );

  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "section",
      payload: {
        sectionId: section.id,
        sectionName: title,
        sourceCanvasId: canvasId,
        files: sectionFilePayloads,
      },
    }),
    [section.id, title, canvasId, sectionFilePayloads],
  );

  const draggable = useDraggable({
    id: `section-row:files-rail-context:${section.id}`,
    data: draggableData,
  });

  return (
    // Starts collapsed: expanding the canvas reveals its section rows, and
    // each section opens on its own click (one level per click, no skipping).
    <RailExpandableRow
      icon={Layers}
      label={title}
      dragRef={draggable.setNodeRef}
      dragProps={{ ...draggable.attributes, ...draggable.listeners }}
      isDragging={draggable.isDragging}
    >
      <div className="flex flex-col">{children}</div>
    </RailExpandableRow>
  );
}

/**
 * List-item host for context file rows. Contiguous selection chrome keys off
 * this element (the direct child of the file list); the inner FilesRailFileRow
 * only carries the painted chrome outline.
 */
function FilesRailContextFileRow({
  record,
  scope,
  sourcePlaced,
  sourceHidden,
  sourceParentSectionId,
  dragNamespace,
  menuActions,
}: {
  record: FileRecord;
  scope: { canvasId: string } | { flowSessionId: string };
  sourcePlaced?: boolean;
  /** The file's node is `hidden: true` — a full member, just not rendered. */
  sourceHidden?: boolean;
  sourceParentSectionId?: string | null;
  dragNamespace: string;
  menuActions: FilesRailFileMenuActions;
}) {
  const activeFileRecordId = useActiveFileRecordId();
  const openFileRecord = useOpenFileRecord();
  const active = activeFileRecordId === record.id;
  const dimmed = sourcePlaced === false || sourceHidden === true;

  // listItem=false: host attrs live on this wrapper, not the inner row.
  const { selected } = useRailRowSelection(record.id, "context", "file", false);

  return (
    <div
      {...{ [RAIL_LIST_ITEM_ATTR]: "" }}
      {...(selected ? { [RAIL_SELECTED_ATTR]: "" } : {})}
      className={cn(dimmed && "opacity-60")}
      title={dimmed ? "Not placed on canvas" : undefined}
    >
      <FilesRailContextDraggableFileRow
        record={record}
        scope={scope}
        dragNamespace={dragNamespace}
        active={active}
        listItem={false}
        onOpen={() =>
          openFileRecord(record.id, {
            scope:
              "canvasId" in scope
                ? { class: "canvas", canvas_id: scope.canvasId }
                : {
                    class: "flow-session",
                    flow_session_id: scope.flowSessionId,
                  },
          })
        }
        sourcePlaced={sourcePlaced}
        sourceParentSectionId={sourceParentSectionId}
        menuActions={menuActions}
      />
    </div>
  );
}

export function FilesRailCanvasContextBody({ canvasId }: { canvasId: string }) {
  const contents = useCanvasRailContents(canvasId, "", { filesOnly: true });
  const { sectionView, isContentsLoading, fileRecords, fileRecordIdToNodeId } =
    contents;

  const { menuActions, dialogs } = useFilesRailContextFileActions({
    allRecords: fileRecords,
    fileRecordIdToNodeId,
  });

  const renderFileItem = (item: ContentItem, dragNamespace: string) => {
    const record = item.fileRecord;
    if (!record) return null;
    return (
      <FilesRailContextFileRow
        key={item.id}
        record={record}
        scope={{ canvasId }}
        sourcePlaced={item.placed}
        sourceHidden={item.hidden}
        sourceParentSectionId={
          item.placed ? (item.parentSectionId ?? null) : undefined
        }
        dragNamespace={dragNamespace}
        menuActions={menuActions}
      />
    );
  };

  if (isContentsLoading) {
    return <p className="px-2 py-1 text-sm text-n-40">Loading canvas files…</p>;
  }

  const hasBody =
    sectionView.entries.some((e) => e.items.length > 0) ||
    sectionView.rootItems.length > 0;

  if (!hasBody) {
    return (
      <p className="px-2 py-1 text-sm text-n-40">No files on this canvas.</p>
    );
  }

  return (
    <>
      {dialogs}
      <div className="flex flex-col">
        {sectionView.entries.map(({ section, items }) =>
          items.length === 0 ? null : (
            <FilesRailContextSectionGroup
              key={section.id}
              section={section}
              items={items}
              canvasId={canvasId}
            >
              {items.map((item) =>
                renderFileItem(
                  item,
                  `files-rail-context:section:${section.id}`,
                ),
              )}
            </FilesRailContextSectionGroup>
          ),
        )}
        {sectionView.rootItems.map((item) =>
          renderFileItem(item, "files-rail-context:root"),
        )}
      </div>
    </>
  );
}

/**
 * Flat, backend-driven list of a canvas's file records. Unlike
 * [`FilesRailCanvasContextBody`], this reads only from the file-records API
 * (`useCanvasFileRecords`) — no Liveblocks — so it loads when the canvas is
 * *not* open (e.g. nested under a canvas row in the workspace rail). No section
 * grouping or placed/stash distinction: just the files.
 */
export function FilesRailCanvasFilesList({ canvasId }: { canvasId: string }) {
  const { data: fileRecords = [], isPending } = useCanvasFileRecords(canvasId);

  const { menuActions, dialogs } = useFilesRailContextFileActions({
    allRecords: fileRecords,
  });

  if (isPending) {
    return <p className="px-2 py-1 text-sm text-n-40">Loading canvas files…</p>;
  }

  if (fileRecords.length === 0) {
    return (
      <p className="px-2 py-1 text-sm text-n-40">No files on this canvas.</p>
    );
  }

  return (
    <>
      {dialogs}
      <div className="flex flex-col">
        {fileRecords.map((record) => (
          <FilesRailContextFileRow
            key={record.id}
            record={record}
            scope={{ canvasId }}
            dragNamespace="files-rail-context:canvas"
            menuActions={menuActions}
          />
        ))}
      </div>
    </>
  );
}

export function FilesRailChatContextBody({
  flowSessionId,
}: {
  flowSessionId: string;
}) {
  const { data: fileRecords = [], isPending } =
    useFlowSessionFileRecords(flowSessionId);

  const { menuActions, dialogs } = useFilesRailContextFileActions({
    allRecords: fileRecords,
  });

  if (isPending) {
    return <p className="px-2 py-1 text-sm text-n-40">Loading chat files…</p>;
  }

  if (fileRecords.length === 0) {
    return (
      <p className="px-2 py-1 text-sm text-n-40">No files in this chat.</p>
    );
  }

  return (
    <>
      {dialogs}
      <div className="flex flex-col">
        {fileRecords.map((record) => (
          <FilesRailContextFileRow
            key={record.id}
            record={record}
            scope={{ flowSessionId }}
            dragNamespace="files-rail-context:chat"
            menuActions={menuActions}
          />
        ))}
      </div>
    </>
  );
}
